import { useGetBankProducts } from '@modules/banks/hooks/queries/useGetBankProducts';
import { Box, Flex } from '@huspy/briks-web';
import { useState } from 'react';
import { BankProduct } from '@modules/banks/api/types';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { BankProductsTable } from './BankProductsTable';
import { ControlPanel } from './ControlPanel';
import { ControlPanelValuesType, ControlKey } from './types';
import { TablePagination } from './Pagination';

const PAGE_SIZE = 10;

const convertPageToOffset = (page: number) => (page - 1) * PAGE_SIZE;

export const BankProducts = ({
  handleOnChange,
  maxProductsSelection,
  tableClassName,
  selectedBankProducts,
  initialValues,
  financedFeeAmount = 0,
  isSimplifiedView,
}: {
  handleOnChange?: Function,
  maxProductsSelection?: number,
  tableClassName?: string,
  selectedBankProducts?: BankProduct[],
  initialValues?: Partial<ControlPanelValuesType>,
  financedFeeAmount?: number,
  isSimplifiedView?: boolean,
}) => {
  const [controlPanelValues, setControlPanelValues] = useState<Partial<ControlPanelValuesType>>(initialValues ?? {});
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading } = useGetBankProducts({
    offset: convertPageToOffset(currentPage),
    ...controlPanelValues,
    financed_fee_amount: financedFeeAmount,
  });

  const handleOnControlPanelUpdate = (key: ControlKey, value: string[]) => {
    const newFilters = {
      ...controlPanelValues,
      [key]: value,
    };

    setControlPanelValues(newFilters);

    // Track filtering
    trackAmplitudeEvent(USER_EVENTS.PROPOSAL.BANK_PRODUCTS_FILTERED, {
      useContext: true,
      filters: JSON.stringify(newFilters),
      bank_products: selectedBankProducts?.map((bankProducts) => bankProducts.external_id) ?? [],
    });
  };

  const handleOnPageSelect = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Flex direction='column'>
      <ControlPanel
        values={ controlPanelValues as ControlPanelValuesType }
        onChange={ handleOnControlPanelUpdate }
      />

      <Box
        className={ tableClassName || '' }
        mt='5'
      >
        <BankProductsTable
          data={ data }
          isLoading={ isLoading }
          handleOnChange={ handleOnChange }
          maxProductsSelection={ maxProductsSelection }
          selectedBankProducts={ selectedBankProducts }
          isSimplifiedView={ isSimplifiedView }
        />
      </Box>

      { !isLoading && data
      && (
        <TablePagination
          total={ data.count }
          currentPage={ currentPage }
          pageSize={ PAGE_SIZE }
          handlePageSelect={ (value: number) => handleOnPageSelect(value) }
        />
      )}
    </Flex>
  );
};
